'use client';

import { useQuery, type QueryClient } from '@tanstack/react-query';

/**
 * Query key factory for session health, per the repo convention that every
 * hook file owns its keys rather than callers inventing them inline.
 */
export const sessionHealthKeys = {
    all: ['session-health'] as const,
    status: () => [...sessionHealthKeys.all, 'status'] as const,
};

export interface SessionHealth {
    /**
     * True once the server session is considered DEAD rather than momentarily
     * unreachable — see {@link SESSION_FAILURES_BEFORE_EXPIRED}.
     *
     * This is a statement about the SERVER SESSION COOKIE, not about Firebase.
     * The Firebase SDK is typically still signed in when this flips, which is
     * precisely why nothing else notices: the app looks authenticated while
     * every server write is rejected.
     */
    readonly expired: boolean;
    /** Consecutive failed re-mint attempts. Reset to 0 by any success. */
    readonly consecutiveFailures: number;
}

export const INITIAL_SESSION_HEALTH: SessionHealth = {
    expired: false,
    consecutiveFailures: 0,
};

/**
 * How many consecutive failed re-mints mean the session is genuinely dead.
 *
 * NOT 1. A single failure is indistinguishable from a dropped connection, a
 * sleeping laptop or a transient 5xx, and announcing "your session expired" for
 * one of those trains people to ignore the message — the failure mode this is
 * meant to fix, not reproduce. Three consecutive failures across the keep-alive
 * cadence is no longer plausibly transient.
 */
export const SESSION_FAILURES_BEFORE_EXPIRED = 3;

/**
 * Record the outcome of a session re-mint attempt.
 *
 * Lives here beside the key and the threshold so the state machine is in ONE
 * place: the keep-alive hook decides WHEN to call this, never what it means.
 *
 * Exported as a plain function over a `QueryClient` rather than a hook because
 * its caller runs inside an effect callback, where hooks cannot.
 */
export function recordSessionRefreshOutcome(
    queryClient: QueryClient,
    succeeded: boolean,
): SessionHealth {
    const previous =
        queryClient.getQueryData<SessionHealth>(sessionHealthKeys.status()) ??
        INITIAL_SESSION_HEALTH;

    // A success is unambiguous recovery: clear the count AND the expired flag,
    // so a session repaired by a fresh sign-in takes the banner down without
    // needing a reload.
    const next: SessionHealth = succeeded
        ? INITIAL_SESSION_HEALTH
        : (() => {
              const consecutiveFailures = previous.consecutiveFailures + 1;
              return {
                  consecutiveFailures,
                  expired:
                      previous.expired ||
                      consecutiveFailures >= SESSION_FAILURES_BEFORE_EXPIRED,
              };
          })();

    queryClient.setQueryData(sessionHealthKeys.status(), next);
    return next;
}

/**
 * Read session health.
 *
 * `staleTime: Infinity` and no network `queryFn`: this key is a CACHE SLOT
 * written by {@link recordSessionRefreshOutcome}, not a fetch. Using the query
 * cache rather than a bespoke store is deliberate — it makes the value reactive
 * in every app for free, and it is already the app-wide state mechanism here.
 */
export const useSessionHealth = (): SessionHealth => {
    const { data } = useQuery({
        queryKey: sessionHealthKeys.status(),
        queryFn: () => INITIAL_SESSION_HEALTH,
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    return data ?? INITIAL_SESSION_HEALTH;
};
